import {
  Phone,
  Mail,
  Building,
  Clock,
  MessageCircle,
  ExternalLink,
  MapPin,
} from 'lucide-react'
import { useApp } from '../context/app-context'
import { t } from '../i18n'
import SEO from '../components/SEO'

const WHATSAPP_URL = 'https://wa.me/201028137221'
const PHONE_EG = '+20 102 813 7221'
const PHONE_SA = '+966 55 746 6681'

const BRANCHES = [
  { nameAr: 'كفر الشيخ، مصر', nameEn: 'Kafr El-Sheikh, Egypt (HQ)', href: 'https://maps.google.com/?q=هيئة+قضاة+الدولة+كفر+الشيخ+مصر', tagAr: 'المقر الرئيسي', tagEn: 'Head Office' },
  { nameAr: '6 أكتوبر، الجيزة، مصر', nameEn: '6th of October, Giza, Egypt', href: 'https://maps.google.com/?q=السادس+من+أكتوبر+الجيزة+مصر', tagAr: 'فرع', tagEn: 'Branch' },
  { nameAr: 'أركان التميز، السعودية', nameEn: 'Arkan Al-Tamayyuz, KSA', href: 'https://maps.google.com/?q=أركان+التميز+السعودية', tagAr: 'فرع', tagEn: 'Branch' },
  { nameAr: 'توب بيراميدز، البحرين', nameEn: 'Top Pyramids, Bahrain', href: 'https://maps.google.com/?q=توب+بيراميدز+البحرين', tagAr: 'فرع', tagEn: 'Branch' },
]

export default function Contact() {
  const { lang } = useApp()
  const isAr = lang === 'ar'

  const channels = [
    {
      icon: <Phone size={22} aria-hidden="true" />,
      label: t(lang, 'phone'),
      lines: [
        { text: PHONE_EG, href: 'tel:+201028137221', dir: 'ltr' as const, tag: isAr ? 'مصر' : 'Egypt' },
        { text: PHONE_SA, href: 'tel:+966557466681', dir: 'ltr' as const, tag: isAr ? 'السعودية' : 'Saudi' },
      ],
    },
    {
      icon: <Mail size={22} aria-hidden="true" />,
      label: t(lang, 'email'),
      lines: [
        { text: 'shelillogistics@gmail.com', href: 'mailto:shelillogistics@gmail.com' },
        { text: 'info@ercanaltamiz.com', href: 'mailto:info@ercanaltamiz.com' },
      ],
    },
    {
      icon: <Building size={22} aria-hidden="true" />,
      label: t(lang, 'branchesTitle'),
      lines: BRANCHES.map(b => ({ text: isAr ? b.nameAr : b.nameEn, href: b.href, external: true })),
    },
    {
      icon: <Clock size={22} aria-hidden="true" />,
      label: t(lang, 'workingHours'),
      lines: [{ text: isAr ? 'متاحون 24/7 - نرد خلال أقل من ساعة' : '24/7 available - replies within the hour', href: WHATSAPP_URL, external: true }],
    },
  ]

  return (
    <main className="page contact-page" dir={isAr ? 'rtl' : 'ltr'}>
      <SEO
        title={t(lang, 'contactHeroTitle')}
        description={t(lang, 'contactHeroDesc')}
        url="https://shelil-logistics-services.pages.dev/contact"
      />

      {/* ===== HERO ===== */}
      <section className="contact-hero page-rise">
        <div className="contact-hero-inner">
          <h1 className="contact-hero-title">{t(lang, 'contactHeroTitle')}</h1>
          <p className="contact-hero-desc">{t(lang, 'contactHeroDesc')}</p>
          <div className="hero-btns" style={{ justifyContent: 'center' }}>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              <MessageCircle size={18} aria-hidden="true" />
              {isAr ? 'واتساب الآن' : 'WhatsApp Now'}
            </a>
            <a href="tel:+201028137221" className="btn btn-outline">
              <Phone size={18} aria-hidden="true" />
              {isAr ? 'اتصل بنا' : 'Call Us'}
            </a>
          </div>
        </div>
      </section>

      {/* ===== CHANNELS ===== */}
      <section className="section contact-channels">
        <div className="section-inner">
          <div className="section-header">
            <h2>{t(lang, 'contactInfoTitle')}</h2>
            <p>
              {isAr
                ? 'كل قنواتنا أمامك. لا نماذج ولا انتظار - تواصل مباشرة مع الفريق.'
                : 'Every channel, one tap away. No forms, no queues - reach the team directly.'}
            </p>
          </div>

          <div className="channel-grid">
            {channels.map((card, i) => (
              <div key={card.label} className="channel-card page-rise" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="channel-icon" aria-hidden="true">{card.icon}</div>
                <div className="channel-body">
                  <span className="channel-label">{card.label}</span>
                  <div className="channel-lines">
                    {card.lines.map(line => {
                      const external = 'external' in line && line.external
                      return (
                        <a
                          key={line.text}
                          href={line.href}
                          className="channel-link"
                          dir={'dir' in line ? line.dir : undefined}
                          target={external ? '_blank' : undefined}
                          rel={external ? 'noopener noreferrer' : undefined}
                        >
                          {line.text}
                          {'tag' in line && line.tag && <span className="channel-tag">{line.tag}</span>}
                        </a>
                      )
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="contact-wa-strip page-rise" style={{ animationDelay: '0.35s' }}>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-whatsapp-big">
              <MessageCircle size={20} aria-hidden="true" />
              {t(lang, 'whatsAppTitle')}
            </a>
          </div>
        </div>
      </section>

      {/* ===== BRANCHES + MAP ===== */}
      <section className="section contact-branches">
        <div className="section-inner">
          <div className="section-header">
            <h2>{t(lang, 'branchesTitle')}</h2>
            <p>{isAr ? 'نصل إليك من أربع نقاط استراتيجية في مصر والخليج' : 'We reach you from four strategic points in Egypt and the Gulf'}</p>
          </div>

          <div className="branch-layout">
            <div className="branch-map page-rise">
              <iframe
                title={isAr ? 'موقعنا على الخريطة' : 'Our location on the map'}
                src="https://maps.google.com/maps?q=مدينة+السادس+من+أكتوبر+الجيزة+مصر&t=&z=12&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, position: 'relative', zIndex: 1 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href="https://maps.google.com/?q=السادس+من+أكتوبر+الجيزة+مصر"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-map-fallback"
              >
                <MapPin size={14} aria-hidden="true" />
                {isAr ? 'فتح على خرائط جوجل' : 'Open in Google Maps'}
                <ExternalLink size={13} aria-hidden="true" />
              </a>
            </div>

            <ul className="branch-list">
              {BRANCHES.map((b, i) => (
                <li key={b.nameEn} className="branch-row page-rise" style={{ animationDelay: `${i * 80 + 100}ms` }}>
                  <div className="branch-row-icon" aria-hidden="true"><MapPin size={18} /></div>
                  <div className="branch-row-text">
                    <strong>{isAr ? b.nameAr : b.nameEn}</strong>
                    <span>{isAr ? b.tagAr : b.tagEn} · {isAr ? 'شليل للخدمات اللوجستية' : 'Shalil Logistics'}</span>
                  </div>
                  <a href={b.href} target="_blank" rel="noopener noreferrer" className="branch-row-link">
                    {isAr ? 'الخريطة' : 'Map'}
                    <ExternalLink size={13} aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="contact-cta">
        <div className="contact-cta-inner">
          <div className="contact-cta-icon" aria-hidden="true"><MessageCircle size={32} /></div>
          <div className="contact-cta-text">
            <h2>{t(lang, 'whatsAppTitle')}</h2>
            <p>{t(lang, 'whatsAppDesc')}</p>
          </div>
          <div className="contact-cta-btns">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-whatsapp-big">
              <MessageCircle size={18} aria-hidden="true" /> WhatsApp
            </a>
            <a href="tel:+201028137221" className="btn-outline-contact">
              <Phone size={18} aria-hidden="true" />
              {isAr ? 'اتصل بنا' : 'Call Us'}
            </a>
          </div>
        </div>
      </section>
    </main>
  )
}
